<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import ChatAssistant from "../components/ChatAssistant.vue";
import ConfirmModal from "../components/ConfirmModal.vue";
import StatusModal from "../components/StatusModal.vue";
import SupplierModal from "../components/SupplierModal.vue";
import {
  listSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier
} from "../db/suppliers.js";

const router = useRouter();
const loading = ref(false);
const suppliers = ref([]);
const search = ref("");

// Search Suggestions
const suggestOpen = ref(false);
const supplierSuggestions = ref([]);

// Advanced Filters
const filtersOpen = ref(false);
const sortBy = ref("name_asc");
const filterHasEmail = ref(false);

// Pagination
const currentPage = ref(1);
const itemsPerPage = 10;

// Supplier Modal State
const showSupplierModal = ref(false);
const isEditing = ref(false);
const modalLoading = ref(false);
const form = ref({ id_suppliers: null, name: "", phone: "", email: "", address: "", image_url: null });

// Confirmation & Status Modals State
const showConfirmModal = ref(false);
const confirmModal = ref({
  title: '',
  message: '',
  onConfirm: () => { },
  onCancel: () => { },
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  type: 'warning'
});

const showStatusModal = ref(false);
const statusModal = ref({ title: '', message: '', type: 'success' });

// Helper to replace Swal.fire confirmation
function confirmAction({ title, message, confirmText, cancelText, type }) {
  return new Promise((resolve) => {
    confirmModal.value = {
      title,
      message,
      confirmText: confirmText || 'Confirmar',
      cancelText: cancelText || 'Cancelar',
      type: type || 'warning',
      onConfirm: () => {
        showConfirmModal.value = false;
        resolve({ isConfirmed: true });
      },
      onCancel: () => {
        showConfirmModal.value = false;
        resolve({ isConfirmed: false });
      }
    };
    showConfirmModal.value = true;
  });
}

// Helper to replace Swal.fire alerts
function showAlert({ title, text, icon }) {
  statusModal.value = {
    title: title || (icon === 'error' ? 'Error' : 'Éxito'),
    message: text || '',
    type: icon === 'error' ? 'error' : 'success'
  };
  showStatusModal.value = true;
}

// Stats Animation
const animTotal = ref(0);
const animContact = ref(0);

function animateNumber(outRef, to, duration = 800) {
  const from = Number(outRef.value || 0);
  const start = performance.now();
  function step(now) {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    outRef.value = from + (to - from) * eased;
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

onMounted(async () => {
  await loadData();
});

async function loadData() {
  loading.value = true;
  try {
    suppliers.value = await listSuppliers();
    updateStats();
  } catch (error) {
    console.error(error);
    showAlert({ title: "Error", text: "No se pudieron cargar los proveedores", icon: "error" });
  } finally {
    loading.value = false;
  }
}

function updateStats() {
  animateNumber(animTotal, suppliers.value.length);
  const withContact = suppliers.value.filter(s => s.phone || s.email).length;
  animateNumber(animContact, withContact);
}

// Search Logic
watch(search, (val) => {
  const term = String(val || '').trim();
  if (!term) {
    supplierSuggestions.value = [];
    suggestOpen.value = false;
    return;
  }
  if (suggestOpen.value) loadSupplierSuggestions(val);
});

function openSuggestions() {
  suggestOpen.value = true;
  loadSupplierSuggestions(search.value);
}

function selectSuggestion(s) {
  search.value = s.name || "";
  suggestOpen.value = false;
}

function loadSupplierSuggestions(q) {
  const term = String(q || "").toLowerCase();
  if (!term) {
    supplierSuggestions.value = [];
    return;
  }

  supplierSuggestions.value = suppliers.value
    .filter(s =>
      (s.name || "").toLowerCase().includes(term) ||
      (s.email || "").toLowerCase().includes(term) ||
      (s.phone || "").toLowerCase().includes(term)
    )
    .slice(0, 8);
}

const filteredSuppliers = computed(() => {
  let list = suppliers.value;

  if (search.value.trim()) {
    const q = search.value.toLowerCase();
    list = list.filter(s =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.phone || "").toLowerCase().includes(q) ||
      (s.address || "").toLowerCase().includes(q)
    );
  }

  if (filterHasEmail.value) {
    list = list.filter(s => s.email && s.email.trim() !== "");
  }

  list = [...list].sort((a, b) => {
    if (sortBy.value === 'name_asc') return a.name.localeCompare(b.name);
    if (sortBy.value === 'name_desc') return b.name.localeCompare(a.name);
    if (sortBy.value === 'id_desc') return b.id_suppliers - a.id_suppliers;
    return 0;
  });

  return list;
});

const paginatedSuppliers = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredSuppliers.value.slice(start, end);
});

const totalPages = computed(() => Math.ceil(filteredSuppliers.value.length / itemsPerPage));

function goToPage(p) {
  if (p >= 1 && p <= totalPages.value) currentPage.value = p;
}

// Modal Actions
function openAddModal() {
  isEditing.value = false;
  form.value = { id_suppliers: null, name: "", phone: "", email: "", address: "", image_url: null };
  showSupplierModal.value = true;
}

function openEditModal(supplier) {
  isEditing.value = true;
  form.value = { ...supplier };
  showSupplierModal.value = true;
}

function closeSupplierModal() {
  showSupplierModal.value = false;
  form.value = { id_suppliers: null, name: "", phone: "", email: "", address: "", image_url: null };
}

async function saveSupplier(formData, imageFile) {
  if (!formData.name.trim()) {
    showAlert({ title: "Atención", text: "El nombre es obligatorio", icon: "warning" });
    return;
  }

  modalLoading.value = true;
  try {
    if (isEditing.value) {
      await updateSupplier(formData.id_suppliers, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        image_url: formData.image_url // Pass this to carry over logic if null (delete)
      }, imageFile);
      showAlert({ title: "Actualizado", text: "Proveedor actualizado correctamente", icon: "success" });
    } else {
      await createSupplier({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address
      }, imageFile);
      showAlert({ title: "Creado", text: "Proveedor creado correctamente", icon: "success" });
    }
    closeSupplierModal();
    await loadData();
  } catch (err) {
    console.error(err);
    showAlert({ title: "Error", text: "Hubo un problema al guardar", icon: "error" });
  } finally {
    modalLoading.value = false;
  }
}

async function deleteItem(supplier) {
  const res = await confirmAction({
    title: "¿Estás seguro?",
    message: `Se eliminará a ${supplier.name}. Esta acción no se puede deshacer.`,
    type: "warning",
    confirmText: "Sí, eliminar",
    cancelText: "Cancelar"
  });

  if (res.isConfirmed) {
    try {
      await deleteSupplier(supplier.id_suppliers);
      showAlert({ title: "Eliminado", text: "Proveedor eliminado", icon: "success" });
      await loadData();
    } catch (err) {
      showAlert({ title: "Error", text: "No se pudo eliminar el proveedor", icon: "error" });
    }
  }
}

function goBack() {
  router.push("/dashboard");
}
</script>

<template>
  <div class="page-container">
    <header class="page-header">
      <div class="header-left">
        <button @click="goBack" class="back-btn" title="Volver">
          <span class="material-symbols-rounded">arrow_back</span>
        </button>
        <div class="title-group">
          <h1 class="page-title">Proveedores</h1>
          <p class="page-subtitle">Gestión de cadena de suministro</p>
        </div>
      </div>
      <button class="btn-primary" @click="openAddModal">
        <span class="material-symbols-rounded">add</span>
        Nuevo Proveedor
      </button>
    </header>

    <main class="content-wrapper">
      <!-- Colored Stats Logic derived from SalesPage -->
      <section class="stats-grid">
        <div class="stat-card orange">
          <span class="material-symbols-rounded stat-icon">local_shipping</span>
          <div class="stat-content">
            <span class="stat-label">Total Proveedores</span>
            <span class="stat-value">{{ Math.round(animTotal) }}</span>
            <span class="stat-sub">Registrados</span>
          </div>
        </div>

        <div class="stat-card blue">
          <span class="material-symbols-rounded stat-icon">contact_phone</span>
          <div class="stat-content">
            <span class="stat-label">Con Contacto</span>
            <span class="stat-value">{{ Math.round(animContact) }}</span>
            <span class="stat-sub">Email o Teléfono</span>
          </div>
        </div>
      </section>

      <!-- Advanced Filters & Table -->
      <section class="main-card">
        <div class="filters-bar">
          <div class="filter-row">
            <!-- Dropdown Search -->
            <div class="search-wrap" ref="searchWrapRef">
              <div class="input-group">
                <span class="material-symbols-rounded search-icon">search</span>
                <input type="text" v-model="search" class="filter-input search-input" placeholder="Buscar proveedor..."
                  @focus="openSuggestions" @input="suggestOpen = true" @keydown.escape="suggestOpen = false"
                  @blur="setTimeout(() => suggestOpen = false, 120)" />
                <button v-if="search" class="clear-search" @click="search = ''">
                  <span class="material-symbols-rounded">close</span>
                </button>
              </div>

              <div v-if="suggestOpen" class="suggest-backdrop" @click="suggestOpen = false"></div>
              <div v-if="suggestOpen" class="suggest-box">
                <ul class="suggest-list">
                  <li v-for="s in supplierSuggestions" :key="s.id_suppliers" class="suggest-item"
                    @mousedown.prevent="selectSuggestion(s)">
                    <div class="suggest-avatar">
                      <img v-if="s.image_url" :src="s.image_url" :alt="s.name" class="avatar-full" />
                      <span v-else>{{ s.name.charAt(0).toUpperCase() }}</span>
                    </div>
                    <div class="suggest-info">
                      <div class="suggest-name">{{ s.name }}</div>
                      <div class="suggest-sub" v-if="s.email || s.phone">
                        {{ s.email || s.phone }}
                      </div>
                    </div>
                  </li>
                  <li v-if="!supplierSuggestions.length" class="suggest-empty">
                    Sin resultados
                  </li>
                </ul>
              </div>
            </div>

            <!-- Advanced Filters Toggle -->
            <button class="btn-secondary filter-toggle" @click="filtersOpen = !filtersOpen"
              :class="{ 'active': filtersOpen }">
              <span class="material-symbols-rounded">tune</span>
              Filtros
            </button>
          </div>

          <!-- Collapsible Filters -->
          <div class="advanced-filters" v-if="filtersOpen">
            <div class="filter-group">
              <label>Ordenar por</label>
              <select v-model="sortBy" class="custom-select">
                <option value="name_asc">Nombre (A-Z)</option>
                <option value="name_desc">Nombre (Z-A)</option>
                <option value="id_desc">Recientes</option>
              </select>
            </div>

            <div class="filter-group checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="filterHasEmail">
                <span>Solo con Email</span>
              </label>
            </div>
          </div>
        </div>

        <div class="table-container">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Cargando proveedores...</p>
          </div>

          <div v-else-if="filteredSuppliers.length === 0" class="empty-state">
            <span class="material-symbols-rounded empty-icon">inventory_2</span>
            <h3>No se encontraron proveedores</h3>
            <p v-if="search">Intenta con otra búsqueda</p>
            <p v-else>Agrega tu primer proveedor para comenzar</p>
          </div>

          <table v-else class="modern-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Contacto</th>
                <th>Dirección</th>
                <th class="actions-col">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sup in paginatedSuppliers" :key="sup.id_suppliers">
                <td>
                  <div class="name-cell">
                    <div v-if="sup.image_url" class="avatar-img">
                      <img :src="sup.image_url" :alt="sup.name" />
                    </div>
                    <div v-else class="avatar-ph">
                      {{ sup.name.charAt(0).toUpperCase() }}
                    </div>
                    <span class="name-text">{{ sup.name }}</span>
                  </div>
                </td>
                <td>
                  <div class="contact-info">
                    <div v-if="sup.email" class="contact-row">
                      <span class="material-symbols-rounded tiny-icon">mail</span>
                      {{ sup.email }}
                    </div>
                    <div v-if="sup.phone" class="contact-row">
                      <span class="material-symbols-rounded tiny-icon">call</span>
                      {{ sup.phone }}
                    </div>
                    <span v-if="!sup.email && !sup.phone" class="muted">—</span>
                  </div>
                </td>
                <td>
                  <div class="address-cell" :title="sup.address">
                    {{ sup.address || "—" }}
                  </div>
                </td>
                <td>
                  <div class="actions-cell">
                    <button class="btn-icon edit" @click="openEditModal(sup)" title="Editar">
                      <span class="material-symbols-rounded">edit</span>
                    </button>
                    <button class="btn-icon delete" @click="deleteItem(sup)" title="Eliminar">
                      <span class="material-symbols-rounded">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination-bar">
          <button class="page-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
            <span class="material-symbols-rounded">chevron_left</span>
          </button>
          <span class="page-info">Página {{ currentPage }} de {{ totalPages }}</span>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
            <span class="material-symbols-rounded">chevron_right</span>
          </button>
        </div>
      </section>
    </main>

    <!-- Modals -->
    <SupplierModal :show="showSupplierModal" :is-editing="isEditing" :initial-data="form" :loading="modalLoading"
      @close="closeSupplierModal" @save="saveSupplier" />

    <ConfirmModal :show="showConfirmModal" :title="confirmModal.title" :message="confirmModal.message"
      :confirm-text="confirmModal.confirmText" :cancel-text="confirmModal.cancelText" :type="confirmModal.type"
      @confirm="confirmModal.onConfirm" @cancel="confirmModal.onCancel" />

    <StatusModal :show="showStatusModal" :title="statusModal.title" :message="statusModal.message"
      :type="statusModal.type" @close="showStatusModal = false" />

    <ChatAssistant />
  </div>
</template>

<style scoped>
/* Core Page Layout */
.page-container {
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
  min-height: 100vh;
  background-color: var(--background);
  color: var(--text);
  display: flex;
  flex-direction: column;
}

.page-header {
  background: var(--surface);
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(10px);
  flex-wrap: wrap;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-btn {
  background: transparent;
  border: none;
  color: var(--text);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;
  display: grid;
  place-items: center;
}

.back-btn:hover {
  background: var(--surface-2);
}

.title-group {
  display: flex;
  flex-direction: column;
}

.page-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.page-subtitle {
  margin: 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.content-wrapper {
  flex: 1;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Stats Cards - Matches IngredientesPage.vue */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  color: #fff;
  border: none;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  min-height: 92px;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  font-size: 28px;
  background: rgba(255, 255, 255, 0.22);
  padding: 10px 12px;
  border-radius: 12px;
  /* Reset previous position absolute if it was there */
  position: static;
  opacity: 1;
  transform: none;
}

.stat-content {
  display: grid;
  gap: 6px;
  z-index: 1;
}

.stat-label {
  font-size: 0.8rem;
  opacity: 0.9;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-weight: 700;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1;
}

.stat-sub {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-top: 0;
}

/* Gradients from IngredientesPage */
.stat-card.orange {
  background: linear-gradient(135deg, #f59e0b, #f97316);
}

.stat-card.blue {
  background: linear-gradient(135deg, #3b82f6, #4f46e5);
}

/* Main Card (Filters + Table) */
.main-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  flex: 1;
}

.filters-bar {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.filter-row {
  display: flex;
  gap: 1rem;
  align-items: center;
}

/* Dropdown Search Styles */
.search-wrap {
  position: relative;
  flex: 1;
  max-width: 500px;
}

.input-group {
  display: flex;
  align-items: center;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: 0.5rem 1rem;
  gap: 0.5rem;
  transition: all 0.2s;
}

.input-group:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 2px rgba(198, 146, 61, 0.1);
}

.search-input {
  border: none;
  background: transparent;
  width: 100%;
  color: var(--text);
  outline: none;
  font-size: 0.95rem;
}

.search-icon {
  color: var(--muted);
}

.clear-search {
  background: transparent;
  border: none;
  color: var(--muted);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

.clear-search:hover {
  color: var(--text);
}

/* Suggestion Dropdown */
.suggest-box {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.5rem;
  background: var(--panel-bg);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15);
  z-index: 50;
  overflow: hidden;
}

.suggest-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
}

.suggest-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid var(--border);
}

.suggest-item:last-child {
  border-bottom: none;
}

.suggest-item:hover {
  background: var(--surface-2);
}

.suggest-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--surface-2);
  color: var(--text);
  display: grid;
  place-items: center;
  font-weight: 600;
  font-size: 0.85rem;
}

.suggest-info {
  display: flex;
  flex-direction: column;
}

.suggest-name {
  font-weight: 500;
  color: var(--text);
}

.suggest-sub {
  font-size: 0.8rem;
  color: var(--muted);
}

.suggest-empty {
  padding: 1.5rem;
  text-align: center;
  color: var(--muted);
}

/* Advanced Filters */
.filter-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-toggle.active {
  background: var(--surface-2);
  border-color: var(--accent);
  color: var(--accent);
}

.advanced-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1rem;
  background: var(--surface-2);
  border-radius: 0.75rem;
  border: 1px solid var(--border);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.filter-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
}

.custom-select {
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--bg);
  color: var(--text);
  font-size: 0.9rem;
  cursor: pointer;
}

.checkbox-group {
  justify-content: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: var(--text);
  font-size: 0.95rem;
}

/* Table Styles - Preserved */
.table-container {
  overflow-x: auto;
  flex: 1;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.modern-table th {
  padding: 1rem 1.5rem;
  color: var(--muted);
  font-weight: 600;
  font-size: 0.85rem;
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
}

.modern-table td {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid var(--border);
  background: var(--surface-2);
}

.avatar-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-ph {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), #00c2a8);
  color: white;
  display: grid;
  place-items: center;
  font-weight: 600;
  font-size: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.name-text {
  font-weight: 500;
  color: var(--text);
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
}

.contact-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--muted);
}

.tiny-icon {
  font-size: 1rem;
}

.address-cell {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--muted);
  font-size: 0.9rem;
}

.actions-cell {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  padding: 6px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: transparent;
  color: var(--muted);
  transition: all 0.2s;
}

.btn-icon:hover {
  background: var(--surface-2);
  color: var(--text);
}

.btn-icon.edit:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.btn-icon.delete:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

/* Pagination */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  gap: 1rem;
}

.page-btn {
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  cursor: pointer;
  color: var(--text);
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: var(--surface-2);
  border-color: var(--text);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.9rem;
  color: var(--muted);
}

/* Buttons */
.btn-primary {
  background: var(--accent);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(198, 146, 61, 0.25);
}

.btn-secondary {
  background: transparent;
  color: var(--text);
  border: 1px solid var(--border);
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: var(--surface-2);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Utilities */
.spinner,
.spinner-sm {
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner {
  width: 40px;
  height: 40px;
  border-color: var(--border);
  border-top-color: var(--accent);
}

.loading-state {
  padding: 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--muted);
}

.empty-state {
  padding: 4rem;
  text-align: center;
  color: var(--muted);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.muted {
  color: var(--muted);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Responsive Media Queries */
@media (max-width: 768px) {
  .page-header {
    padding: 1rem;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .header-left {
    justify-content: flex-start;
  }

  .btn-primary {
    justify-content: center;
    width: 100%;
  }

  .content-wrapper {
    padding: 1rem;
    gap: 1.5rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-wrap {
    max-width: 100%;
  }

  .filters-bar {
    padding: 1rem;
  }

  .pagination-bar {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
  }
}
</style>
